import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useLdReady } from '@omnea-app/fe-org-settings/useLdReady'
import { useRouteLdFlags } from './launch-darkly'
import type { AvailabilityInputs } from './availability'

export const AvailabilityContext = createContext<AvailabilityInputs | undefined>(undefined)

/**
 * Resolves what the organisation is entitled to, what the deployment has rolled out, and who the
 * caller is, once for every consumer. `isOmnean` comes from the session, which throws when read
 * outside the authenticated tree, so this cannot mount above it.
 */
export const AvailabilityContextProvider = ({ children }: { children: ReactNode }) => {
  const orgFlags = useFeatureFlags()
  const isLdReady = useLdReady()
  const ldFlags = useRouteLdFlags()
  const { isOmnean } = useAuthenticatedUser().user

  const availability = useMemo(
    (): AvailabilityInputs => ({ orgFlags, isLdReady, ldFlags, isOmnean }),
    [orgFlags, isLdReady, ldFlags, isOmnean]
  )

  return <AvailabilityContext.Provider value={availability}>{children}</AvailabilityContext.Provider>
}

export const useAvailabilityContext = (): AvailabilityInputs => {
  const availability = useContext(AvailabilityContext)
  if (availability == null) {
    throw new Error('useAvailabilityContext must be used within an AvailabilityContextProvider')
  }
  return availability
}
